import { ArrowLinkButton } from "~/components/ArrowLinkButton";
import { CityGrid } from "~/components/CityGrid";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { CITY_COORDINATES, type City } from "~/lib/cities";
import type { CityMarker } from "~/components/PolandMap";

const PolandMap = dynamic(() => import("~/components/PolandMap"), {
  ssr: false,
  loading: () => (
    <div className="flex min-h-[420px] items-center justify-center rounded-xl bg-gray-50 text-sm text-gray-400">
      Ładowanie mapy…
    </div>
  ),
});

interface TopCity extends CityMarker {
  slug: string;
  priority: City["priority"];
}

interface Props {
  serviceSlug: string;
  serviceNameGenitive: string;
  cities: City[];
}

export function ServiceCitySection({
  serviceSlug,
  serviceNameGenitive,
  cities,
}: Props) {
  const router = useRouter();
  const prioritizedCities = cities.filter((city) => city.priority !== "P2");
  const citiesForMap = (prioritizedCities.length > 0 ? prioritizedCities : cities)
    .slice(0, 12);
  const topCities: TopCity[] = citiesForMap.map((city) => ({
    ...CITY_COORDINATES[city.slug],
    slug: city.slug,
    name: city.name,
    description:
      city.priority === "P0" ? "Największy rynek" : "Ważne miasto regionalne",
    priority: city.priority,
  }));
  const topCitySlugs = new Set(topCities.map((city) => city.slug));
  const remainingCities = cities.filter((city) => !topCitySlugs.has(city.slug));

  if (cities.length === 0) {
    return null;
  }

  const handleCityClick = (city: CityMarker) => {
    const topCity = topCities.find((c) => c.name === city.name);
    if (topCity) {
      router.push(`/${serviceSlug}/${topCity.slug}/`);
    }
  };

  return (
    <section className="bg-gray-50 py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="mb-2 text-xl font-bold text-[#1B3A5C]">
          Znajdź wykonawcę {serviceNameGenitive} w swoim mieście
        </h2>
        <p className="mb-8 text-sm text-gray-500">
          Pokazujemy tylko miasta, w których ta usługa ma już realne oferty. Kliknij miasto na mapie lub przejdź do pełnej listy niżej.
        </p>

        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="w-full flex-shrink-0 lg:w-[55%]">
            <PolandMap
              cities={topCities}
              center={[52.1, 19.4]}
              zoom={6}
              height="420px"
              onCityClick={handleCityClick}
            />
          </div>

          <div className="flex-1">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Najważniejsze miasta dla tej usługi
            </h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {topCities.map((city) => (
                <ArrowLinkButton
                  key={city.slug}
                  href={`/${serviceSlug}/${city.slug}/`}
                  label={city.name}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {remainingCities.length > 0 && (
        <CityGrid
          serviceSlug={serviceSlug}
          cities={remainingCities}
          title={`Pozostałe miasta dla ${serviceNameGenitive}`}
          subtitle="Dodatkowe lokalizacje z aktywnymi ofertami, których nie ma na liście wyróżnionych miast."
          showAllByDefault
        />
      )}
    </section>
  );
}
